/**
 * Построение плана тренировки.
 *
 * План — это упражнение плюс список слов, для которых запускается Memory Loop.
 * Смесь материала адаптируется к состоянию пользователя до генерации, а не
 * после: иначе пришлось бы отбрасывать уже собранное упражнение.
 */

import {
  DEFAULT_LEARNING_CONFIG,
  adaptMix,
  startMemoryLoop,
  type LearningConfig,
} from '@type2x/learning-engine';
import { generateExercise } from '@type2x/exercise-generator';

import type { PracticePlan, PracticeRequest } from '../contracts/practice.js';
import { ITEMS_PER_MINUTE, MEMORY_LOOP_CAPACITY } from '../contracts/practice.js';
import { buildPools } from './pools.js';

/**
 * Строит персональную тренировку.
 *
 * @param request состояние навыка и параметры сессии;
 * @param config конфигурация обучающего алгоритма.
 */
export function buildPracticePlan(
  request: PracticeRequest,
  config: LearningConfig = DEFAULT_LEARNING_CONFIG,
): PracticePlan {
  const { snapshot, nowMs } = request;

  const decision = adaptMix(
    {
      accuracy: request.accuracy,
      fatigue: request.fatigue,
      stuckRatio: stuckRatio(snapshot),
      effortlessSessions: request.effortlessSessions,
    },
    config,
  );

  const pools = buildPools(snapshot, config, nowMs);
  const itemCount = request.durationMinutes * ITEMS_PER_MINUTE;

  const exercise = generateExercise({
    seed: request.seed,
    mode: 'frequent-words',
    language: snapshot.language,
    layoutId: snapshot.layoutId,
    itemCount,
    mix: decision.mix,
    pools,
    hintLevel: request.hintLevel,
    maxConsecutiveRepeats: config.maxConsecutiveRepeats,
  });

  const memoryLoopWords = pools.weak.slice(0, MEMORY_LOOP_CAPACITY);

  return {
    exercise,
    memoryLoop: memoryLoopWords.map((unit) => startMemoryLoop(unit.content)),
    adaptationReasons: decision.reasons,
    requiredUnitIds: memoryLoopWords.map((unit) => unit.id),
  };
}

/** Доля единиц, где успех так и не достигнут. */
function stuckRatio(snapshot: PracticeRequest['snapshot']): number {
  const stats = Object.values(snapshot.stats);
  if (stats.length === 0) return 0;
  const stuck = stats.filter((stat) => stat.attempts > 0 && stat.consecutiveSuccesses === 0);
  return stuck.length / stats.length;
}
